import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import MiloPanel from "@/components/MiloPanel";
import {
  LayoutDashboard,
  CalendarDays,
  Sparkles,
  Palette,
  LogOut,
  Menu,
  Wand2,
  Megaphone,
  KeyRound,
  Target,
  Coins,
  AlertTriangle,
  TrendingUp,
  FolderKanban,
  Compass,
  LifeBuoy,
  ShieldCheck,
  Clock,
  Infinity as InfinityIcon,
} from "lucide-react";
import { toast } from "sonner";

const nav = [
  { to: "/dashboard", label: "Home", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/income-mode", label: "Income Mode", icon: TrendingUp, id: "nav-income-mode" },
  { to: "/hooks", label: "Smart Hooks", icon: Wand2, id: "nav-hooks" },
  { to: "/ad-creator", label: "Ad Creator", icon: Megaphone, id: "nav-ad-creator" },
  { to: "/campaigns/new", label: "New Campaign", icon: Sparkles, id: "nav-new-campaign" },
  { to: "/projects", label: "Projects", icon: FolderKanban, id: "nav-projects" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, id: "nav-calendar" },
  { to: "/brand", label: "Brand Profile", icon: Palette, id: "nav-brand" },
  { to: "/pricing", label: "Buy Credits", icon: Coins, id: "nav-pricing" },
  { to: "/settings", label: "Settings", icon: KeyRound, id: "nav-settings" },
  { to: "/help", label: "Help", icon: LifeBuoy, id: "nav-help" },
  { to: "/admin", label: "Admin", icon: ShieldCheck, id: "nav-admin", adminOnly: true },
];

function ModeSwitch({ onSwitchGuided }) {
  return (
    <div className="my-1.5 px-1" data-testid="mode-switch">
      <p className="mb-1 px-3 text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground">Mode</p>
      <div className="flex items-center gap-1 rounded-full border border-border bg-secondary p-1">
        <button onClick={onSwitchGuided} data-testid="mode-switch-guided"
          className="flex-1 rounded-full px-3 py-1.5 text-xs font-semibold text-muted-foreground transition-colors hover:bg-background hover:text-foreground">
          Guided
        </button>
        <span data-testid="mode-switch-advanced" aria-current="true"
          className="flex-1 rounded-full bg-primary px-3 py-1.5 text-center text-xs font-semibold text-primary-foreground shadow-sm">
          Advanced
        </span>
      </div>
    </div>
  );
}

function NavItems({ onClick, onSwitchGuided }) {
  const { user } = useAuth();
  const items = nav.filter((n) => !n.adminOnly || user?.is_admin);
  return (
    <nav className="flex flex-col gap-1.5">
      {items.map((n) => {
        const Icon = n.icon;
        return (
          <div key={n.to} className="contents">
            <NavLink
              to={n.to}
              end={n.to === "/dashboard"}
              onClick={onClick}
              data-testid={n.id}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`
              }
            >
              <Icon size={18} />
              {n.label}
            </NavLink>
            {n.to === "/dashboard" && (
              <ModeSwitch onSwitchGuided={() => { onClick?.(); onSwitchGuided?.(); }} />
            )}
          </div>
        );
      })}
    </nav>
  );
}

function MiloButton({ onClick, compact }) {
  return (
    <button onClick={onClick} data-testid="milo-open-btn" title="MILO — your Next Best Move"
      className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 font-semibold text-white shadow-sm transition-transform hover:-translate-y-0.5 active:translate-y-0"
      style={{ background: "#1a2a6c" }}>
      <span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: "#00b09b" }}>
        <Compass size={16} />
      </span>
      {!compact && (
        <span className="flex flex-col items-start leading-none">
          <span className="text-sm">MILO</span>
          <span className="text-[10px] font-normal text-white/70">Next Best Move™</span>
        </span>
      )}
    </button>
  );
}

function Brand() {
  return (
    <div className="flex items-center gap-2.5 px-2 py-1">
      <img src="/logo-mark.png" alt="Hook2Income AI logo" className="h-9 w-9 object-contain" />
      <div className="font-head text-lg font-bold leading-none tracking-tight text-foreground">
        Hook2Income<span style={{ color: "hsl(var(--gold))" }}> AI</span>
      </div>
    </div>
  );
}

function CreditPill({ compact }) {
  const { credits } = useAuth();
  const navigate = useNavigate();
  if (!credits) return null;
  if (credits.unlimited) {
    return (
      <button onClick={() => navigate("/pricing")} data-testid="credit-pill"
        className="flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 text-sm font-semibold text-primary">
        <InfinityIcon size={15} /> Unlimited
      </button>
    );
  }
  const low = credits.low;
  return (
    <button onClick={() => navigate("/pricing")} data-testid="credit-pill"
      title="View plans & top-ups"
      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${
        low ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
            : "bg-primary/10 text-primary hover:bg-primary/20"
      }`}>
      <Coins size={15} />
      <span data-testid="credit-balance">{credits.balance}</span>
      {!compact && <span className="font-normal opacity-80">credits</span>}
    </button>
  );
}

function ActiveProjectBadge() {
  const { activeProject } = useAuth();
  const navigate = useNavigate();
  if (!activeProject) return null;
  return (
    <button onClick={() => navigate("/projects")} data-testid="active-project-badge"
      title="Switch project"
      className="flex w-full items-center gap-2 rounded-xl border border-border bg-secondary/50 px-3 py-2 text-left transition-colors hover:border-primary/40">
      <Target size={15} className="shrink-0 text-primary" />
      <div className="min-w-0">
        <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Active project</p>
        <p className="truncate text-sm font-semibold leading-tight" data-testid="active-project-name">{activeProject.name}</p>
      </div>
    </button>
  );
}

export default function Layout({ children }) {
  const { user, logout, credits, refreshCredits, setUiMode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [miloOpen, setMiloOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const switchToGuided = async () => {
    // Remember exactly where the user was so returning to Advanced Mode lands them back here.
    try { sessionStorage.setItem("h2i_return_to", location.pathname + location.search); } catch (e) { console.warn("Could not save return-to path to sessionStorage:", e); }
    await setUiMode("guided");
    navigate("/journey");
  };

  // Global out-of-credits nudge (fired by the API client on any 402).
  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {};
      toast.error(`Not enough credits${d.cost ? ` — ${d.cost} needed` : ""}. Top up to continue.`);
      refreshCredits();
      navigate("/pricing");
    };
    window.addEventListener("insufficient-credits", handler);
    return () => window.removeEventListener("insufficient-credits", handler);
  }, [navigate, refreshCredits]);

  const showLowBanner = credits && !credits.unlimited && credits.low && !credits.pilot_expired;
  const showPilotEnded = credits && credits.pilot_expired;

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-border bg-card p-4 lg:flex">
        <Brand />
        <div className="mt-6 px-2">
          <CreditPill />
        </div>
        <div className="mt-3 px-2">
          <ActiveProjectBadge />
        </div>
        <div className="mt-3 px-2">
          <MiloButton onClick={() => setMiloOpen(true)} />
        </div>
        <div className="mt-6 flex-1">
          <NavItems onSwitchGuided={switchToGuided} />
        </div>
        <div className="border-t border-border pt-4">
          <div className="mb-3 px-2">
            <p className="truncate text-sm font-semibold text-foreground">{user?.name}</p>
            <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            onClick={doLogout}
            data-testid="logout-btn"
            className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground"
          >
            <LogOut size={18} /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-card/80 px-4 py-3 backdrop-blur-xl lg:hidden">
        <Brand />
        <div className="flex items-center gap-2">
          <MiloButton onClick={() => setMiloOpen(true)} compact />
          <CreditPill compact />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu size={22} />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 bg-card p-4">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <Brand />
              <div className="mt-6 px-2">
                <CreditPill />
              </div>
              <div className="mt-6">
                <NavItems onClick={() => setOpen(false)} onSwitchGuided={switchToGuided} />
              </div>
              <div className="mt-8 border-t border-border pt-4">
                <p className="mb-3 px-2 text-xs text-muted-foreground">{user?.email}</p>
                <Button
                  variant="ghost"
                  onClick={doLogout}
                  className="w-full justify-start gap-3"
                >
                  <LogOut size={18} /> Sign out
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="lg:pl-64">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-8 lg:py-12">
          {showPilotEnded && (
            <div data-testid="pilot-ended-banner"
              className="mb-6 flex flex-col gap-4 rounded-2xl border border-primary/30 bg-primary/5 p-6 text-center sm:flex-row sm:items-center sm:justify-between sm:text-left">
              <div className="flex items-start gap-3 sm:items-center">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Clock size={22} />
                </div>
                <div>
                  <p className="font-head text-lg font-bold text-foreground">Your pilot period has ended</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">
                    Your free pilot credits have expired. Choose a plan to keep creating hooks, scripts and campaigns — your paid credits never expire.
                  </p>
                </div>
              </div>
              <Button onClick={() => navigate("/pricing")} data-testid="pilot-ended-choose-plan-btn"
                className="shrink-0 rounded-full bg-primary px-6 py-5 text-base font-semibold hover:bg-primary/90">
                Choose a plan →
              </Button>
            </div>
          )}
          {showLowBanner && (
            <div data-testid="low-credit-banner"
              className="mb-6 flex flex-col gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-800 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-2.5">
                <AlertTriangle size={18} className="shrink-0" />
                <p className="text-sm font-medium">
                  You're low on credits — {credits.balance} left. Top up so your generations don't stop.
                </p>
              </div>
              <Button size="sm" onClick={() => navigate("/pricing")} data-testid="low-credit-topup-btn"
                className="rounded-full bg-amber-600 text-white hover:bg-amber-700">
                Buy credits
              </Button>
            </div>
          )}
          {children}
        </div>
      </main>
      <MiloPanel open={miloOpen} onOpenChange={setMiloOpen} />
    </div>
  );
}
